import React from 'react';
import { block } from '../utils/cn';
import { Popup } from '../Popup';
import { Button } from '../Button';
import { Link } from '../Link';
import { Icon } from '../Icon';
import { PreviewCloseIcon } from '../icons/PreviewCloseIcon';
import './Popover.css';
export var PopoverBehavior;
(function (PopoverBehavior) {
    PopoverBehavior["Immediate"] = "immediate";
    PopoverBehavior["Delayed"] = "delayed";
    PopoverBehavior["DelayedClosing"] = "delayedClosing";
})(PopoverBehavior || (PopoverBehavior = {}));
const b = block('popover');
export class Popover extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            open: this.props.initialOpen,
        };
        this.controlRef = React.createRef();
        this.delayOpening = null;
        this.delayClosing = null;
        this.closedManually = false;
        this.delayByBehavior = {
            [PopoverBehavior.Immediate]: [0, 0],
            [PopoverBehavior.Delayed]: [300, 300],
            [PopoverBehavior.DelayedClosing]: [0, 300],
        };
        this.shouldBeOpen = false;
        this.openTooltip = () => {
            this.unsetOpeningTimeout();
            this.setTooltipOpen(true);
        };
        this.closeTooltip = () => {
            this.unsetClosingTimeout();
            this.setTooltipOpen(false);
        };
        this.openTooltipDelayed = () => {
            this.delayOpening = null;
            this.openTooltip();
        };
        this.closeTooltipDelayed = () => {
            this.delayClosing = null;
            this.closeTooltip();
        };
        this.getButtonView = (isAction = false) => {
            const { theme } = this.props;
            switch (theme) {
                case 'special':
                    return isAction ? 'normal-contrast' : 'flat-contrast';
                case 'announcement':
                    return isAction ? 'normal-contrast' : 'outlined';
                default:
                    return isAction ? 'normal' : 'flat';
            }
        };
        this.toggleTooltip = () => {
            const nextOpen = !this.state.open;
            if (nextOpen) {
                this.openTooltip();
                this.closedManually = false;
            }
            else {
                this.closeTooltip();
                this.closedManually = true;
            }
        };
        this.onMouseEnter = () => {
            const { disabled } = this.props;
            const [delayOpening] = this.delay;
            const { open } = this.state;
            this.unsetClosingTimeout();
            if (!open && !disabled && !this.closedManually) {
                this.delayOpening = setTimeout(this.openTooltipDelayed, delayOpening);
            }
            else {
                this.shouldBeOpen = true;
            }
        };
        this.onMouseLeave = () => {
            const { autoclosable } = this.props;
            const [, delayClosing] = this.delay;
            if (autoclosable && !this.closedManually && !this.delayClosing) {
                this.unsetOpeningTimeout();
                this.delayClosing = setTimeout(this.closeTooltipDelayed, delayClosing);
            }
            else {
                this.shouldBeOpen = false;
            }
            this.closedManually = false;
        };
        this.onClick = async (event) => {
            const { onClick, disabled } = this.props;
            if (!disabled && (!onClick || (await onClick(event)))) {
                this.toggleTooltip();
            }
        };
        this.onCloseClick = async (event) => {
            const { onCloseClick } = this.props;
            this.closeTooltip();
            onCloseClick === null || onCloseClick === void 0 ? void 0 : onCloseClick(event);
        };
    }
    componentDidUpdate(prevProps) {
        if (prevProps.disabled !== this.props.disabled && this.props.disabled) {
            this.closeTooltip();
        }
        if (prevProps.autoclosable !== this.props.autoclosable &&
            this.props.autoclosable === true &&
            this.shouldBeOpen === false) {
            this.closeTooltip();
        }
    }
    componentWillUnmount() {
        this.unsetOpeningTimeout();
        this.unsetClosingTimeout();
    }
    render() {
        const { openOnHover, className, children, offset, anchorRef, disabled, qa } = this.props;
        if (anchorRef) {
            return this.renderTooltip();
        }
        return (React.createElement("div", { ref: this.controlRef, className: b({ disabled }, className), onMouseEnter: openOnHover ? this.onMouseEnter : undefined, onMouseLeave: openOnHover ? this.onMouseLeave : undefined, style: {
                top: offset.top,
                left: offset.left,
            }, "data-qa": qa },
            React.createElement("span", { onClick: this.onClick }, children),
            this.renderTooltip()));
    }
    setTooltipOpen(open) {
        this.setState({ open });
        this.shouldBeOpen = open;
        const { onOpenChange } = this.props;
        if (onOpenChange) {
            onOpenChange(open);
        }
    }
    get delay() {
        var _a;
        const [defaultDelayOpening, defaultDelayClosing] = this.delayByBehavior[(_a = this.props.behavior) !== null && _a !== void 0 ? _a : PopoverBehavior.Delayed];
        const { delayOpening = defaultDelayOpening, delayClosing = defaultDelayClosing } = this.props;
        return [delayOpening, delayClosing];
    }
    unsetOpeningTimeout() {
        if (this.delayOpening) {
            clearTimeout(this.delayOpening);
            this.delayOpening = null;
        }
    }
    unsetClosingTimeout() {
        if (this.delayClosing) {
            clearTimeout(this.delayClosing);
            this.delayClosing = null;
        }
    }
    renderTooltip() {
        const { open } = this.state;
        const { placement, hasArrow, tooltipOffset, tooltipClassName, theme, size, anchorRef, hasClose, forceLinksAppearance, qa, } = this.props;
        return (React.createElement(Popup, { anchorRef: anchorRef || this.controlRef, className: b('tooltip', {
                theme,
                size,
                ['with-close']: hasClose,
                'force-links-appearance': forceLinksAppearance,
            }, tooltipClassName), open: open, placement: placement, hasArrow: hasArrow, offset: tooltipOffset, onClose: anchorRef ? undefined : this.closeTooltip, qa: qa ? `${qa}-tooltip` : '' },
            React.createElement(React.Fragment, null,
                this.renderTitle(),
                this.renderContent(),
                this.renderLinks(),
                this.renderButtons(),
                this.renderClose())));
    }
    renderTitle() {
        const { title } = this.props;
        if (!title) {
            return null;
        }
        return React.createElement("h3", { className: b('tooltip-title') }, title);
    }
    renderContent() {
        const { title, theme, content, htmlContent, contentClassName } = this.props;
        const secondary = title && theme !== 'announcement';
        if (!htmlContent && !content) {
            return null;
        }
        if (htmlContent) {
            return (React.createElement("div", { className: b('tooltip-content', { secondary }), dangerouslySetInnerHTML: {
                    __html: htmlContent,
                } }));
        }
        if (content) {
            return (React.createElement("div", { className: b('tooltip-content', { secondary }, contentClassName) }, content));
        }
        return null;
    }
    renderLinks() {
        const { links } = this.props;
        if (!links.length) {
            return null;
        }
        return (React.createElement("div", { className: b('tooltip-links') }, links.map((link, index) => {
            const { text, href, target = '_blank', onClick } = link;
            return (React.createElement(React.Fragment, { key: `link-${index}` },
                React.createElement(Link, { href: href, target: target, onClick: onClick, className: b('tooltip-link') }, text),
                React.createElement("br", null)));
        })));
    }
    renderButtons() {
        const { tooltipActionButton, tooltipCancelButton } = this.props;
        if (!tooltipActionButton && !tooltipCancelButton) {
            return null;
        }
        return (React.createElement("div", { className: b('tooltip-buttons') },
            this.renderButton(tooltipActionButton, true),
            this.renderButton(tooltipCancelButton)));
    }
    renderButton(buttonProps, isAction = false) {
        if (!buttonProps) {
            return null;
        }
        const { text, onClick } = buttonProps;
        return (React.createElement(Button, { view: this.getButtonView(isAction), width: "max", onClick: onClick, className: b('tooltip-button') }, text));
    }
    renderClose() {
        const { hasClose } = this.props;
        if (!hasClose) {
            return null;
        }
        return (React.createElement("div", { className: b('tooltip-close') },
            React.createElement(Button, { size: "s", view: "flat-secondary", onClick: this.onCloseClick, extraProps: {
                    'aria-label': 'Close',
                } },
                React.createElement(Icon, { data: PreviewCloseIcon, size: 24 }))));
    }
}
Popover.defaultProps = {
    offset: {},
    links: [],
    placement: ['right', 'bottom'],
    hasArrow: true,
    openOnHover: true,
    autoclosable: true,
    theme: 'info',
    size: 's',
    hasClose: false,
    initialOpen: false,
    disabled: false,
    forceLinksAppearance: true,
};
